import { Box, TextField, Typography, MenuItem } from '@mui/material';
import Imposto from '../../../models/imposto';
import TabelaExtrato from '../../util/tabelas/tabelaExtrato';
import { useEffect, useState, useMemo } from 'react';
import Transacao from '../../../models/transacao';
import { onSnapshot, query, where } from 'firebase/firestore';
import TransacaoDb from '../../../db/transacaoDb';
import { map, filter } from 'lodash';
import { ClasseAtivo, Mercado, Trade } from '../../../models/enum';
import { felixTokens } from '../../../theme';

interface ResultadoTabProp {
  usuarioId: string;
  imposto: Imposto;
}

export default function ResultadoTab({ usuarioId, imposto }: ResultadoTabProp) {
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [filtroAtivo, setFiltroAtivo] = useState('');
  const [filtroClasse, setFiltroClasse] = useState('TODOS');
  const [filtroMercado, setFiltroMercado] = useState('TODOS');
  const [filtroClassificacao, setFiltroClassificacao] = useState('TODOS');

  useEffect(() => {
    if (!!usuarioId)
      return onSnapshot(
        query(
          new TransacaoDb().collection({
            usuarioId: usuarioId,
            transacaoId: '',
          }),
          where(
            'dataTransacao',
            '>=',
            imposto.dataBase.clone().startOf('month').toDate(),
          ),
          where(
            'dataTransacao',
            '<',
            imposto.dataBase.clone().add(1, 'month').startOf('month').toDate(),
          ),
        ),
        (query) => {
          setTransacoes(map(query.docs, (snapshot) => snapshot.data()));
        },
      );
  }, [usuarioId]);

  const transacoesFiltradas = useMemo(() => {
    return filter(transacoes, (t) => {
      let matches = true;
      if (
        filtroAtivo &&
        !t.codigoAtivo.toLowerCase().includes(filtroAtivo.toLowerCase())
      )
        matches = false;
      if (filtroClasse !== 'TODOS' && t.classeAtivo !== filtroClasse)
        matches = false;
      if (filtroMercado !== 'TODOS' && t.mercado !== filtroMercado)
        matches = false;
      if (filtroClassificacao !== 'TODOS') {
        const classificacao =
          t.classeAtivo === ClasseAtivo.FII ||
          t.classeAtivo === ClasseAtivo.FIAGRO
            ? 'FII/Fiagro'
            : t.daytradeSwingtrade === Trade.Daytrade
              ? 'Daytrade'
              : 'Comuns';
        if (classificacao !== filtroClassificacao) matches = false;
      }
      return matches;
    });
  }, [
    transacoes,
    filtroAtivo,
    filtroClasse,
    filtroMercado,
    filtroClassificacao,
  ]);

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Ativo"
          value={filtroAtivo}
          onChange={(e) => setFiltroAtivo(e.target.value)}
          // Ticker é código: entra em mono tabular como na coluna da tabela.
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: felixTokens.fontMono,
              fontVariantNumeric: 'tabular-nums',
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Classe"
          value={filtroClasse}
          onChange={(e) => setFiltroClasse(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="TODOS">Todos</MenuItem>
          {Object.values(ClasseAtivo).map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Mercado"
          value={filtroMercado}
          onChange={(e) => setFiltroMercado(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="TODOS">Todos</MenuItem>
          {Object.values(Mercado).map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Classificação"
          value={filtroClassificacao}
          onChange={(e) => setFiltroClassificacao(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="TODOS">Todos</MenuItem>
          <MenuItem value="FII/Fiagro">FII/Fiagro</MenuItem>
          <MenuItem value="Daytrade">Daytrade</MenuItem>
          <MenuItem value="Comuns">Comuns</MenuItem>
        </TextField>
      </Box>
      <Typography variant="eyebrow" color="text.secondary" mt={2} mb={1}>
        Transações:
      </Typography>
      <TabelaExtrato transacoes={transacoesFiltradas} extendida />
    </Box>
  );
}
